import React, { useEffect, useRef, useState, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { appAbsPath } from '../Utils';
import { Icon } from '@iconify/react';

const Sidebar = () => {
  const location = useLocation();
  const sidebarRef = useRef(null);
  const menuStatesRef = useRef({}); // holds dynamic menu open/close states
  const [menuStates, setMenuStates] = useState({}); // triggers rerenders when toggled

  // helper: toggle a menu state (keeps parity with old class version)
  const toggleMenuState = useCallback((menuState) => {
    const keys = Object.keys(menuStatesRef.current);
    if (menuStatesRef.current[menuState]) {
      menuStatesRef.current = { ...menuStatesRef.current, [menuState]: false };
    } else if (keys.length === 0) {
      menuStatesRef.current = { [menuState]: true };
    } else {
      // close all then open requested
      const newState = {};
      keys.forEach((k) => { newState[k] = false; });
      newState[menuState] = true;
      menuStatesRef.current = newState;
    }
    // update local state to re-render
    setMenuStates({ ...menuStatesRef.current });
  }, []);

  // active path checker (considers nested routes)
  const isPathActive = useCallback((path) => {
    const pathname = location && location.pathname ? location.pathname : window.location.pathname;
    if (!path) return false;
    return pathname === path || pathname.startsWith(path + '/') || pathname.startsWith(path + '?') || pathname.startsWith(path);
  }, [location]);

  // when route changes: remove 'active' class from sidebar (parity with old) and scroll active item into view
  useEffect(() => {
    const sidebarEl = sidebarRef.current;
    if (sidebarEl) {
      sidebarEl.classList.remove('active');
    }

    // small delay to allow DOM to update active classes
    const t = setTimeout(() => {
      const activeItem = sidebarEl ? sidebarEl.querySelector('.nav-item.active') : null;
      if (activeItem && typeof activeItem.scrollIntoView === 'function') {
        activeItem.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }, 50);

    return () => clearTimeout(t);
  }, [location]);

  // hover-open behavior (for sidebar-icon-only body class)
  useEffect(() => {
    const body = document.querySelector('body');
    const sidebarEl = sidebarRef.current;
    if (!sidebarEl) return;

    const navItems = Array.from(sidebarEl.querySelectorAll('.nav-item'));
    const mouseOver = (el) => {
      if (body && body.classList.contains('sidebar-icon-only')) {
        el.classList.add('hover-open');
      }
    };
    const mouseOut = (el) => {
      if (body && body.classList.contains('sidebar-icon-only')) {
        el.classList.remove('hover-open');
      }
    };

    navItems.forEach((el) => {
      const overHandler = () => mouseOver(el);
      const outHandler = () => mouseOut(el);
      el.__overHandler = overHandler;
      el.__outHandler = outHandler;
      el.addEventListener('mouseover', overHandler);
      el.addEventListener('mouseout', outHandler);
    });

    return () => {
      navItems.forEach((el) => {
        if (el.__overHandler) el.removeEventListener('mouseover', el.__overHandler);
        if (el.__outHandler) el.removeEventListener('mouseout', el.__outHandler);
      });
    };
  }, []); // run once on mount

  return (
    <nav className="sidebar sidebar-offcanvas pt-2" id="sidebar" ref={sidebarRef}>
      <ul className="nav">

        <li className={`nav-item ${isPathActive('/portaladmin/projects') ? 'active' : ''}`}>
          <NavLink className="nav-link" to="/portaladmin/projects">
            <Icon icon="system-uicons:list" style={{ width: '38px', height: '38px' }} />
            <span className="menu-title mt-2">All Project</span>
          </NavLink>
        </li>

        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/verifiedprojects`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/verifiedprojects`}>
            <Icon icon="solar:verified-check-linear" style={{ width: '30px', height: '30px' }} />
            <span className="menu-title mt-2">Verified Projects</span>
          </NavLink>
        </li>

        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/updatedprojects`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/updatedprojects`}>
            <Icon icon="system-uicons:duplicate-alt" style={{ width: '30px', height: '30px' }} />
            <span className="menu-title mt-2">Updated Projects</span>
          </NavLink>
        </li>

        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/disabledprojects`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/disabledprojects`}>
            <Icon icon="system-uicons:button-minus" style={{ width: '30px', height: '30px' }} />
            <span className="menu-title mt-2">Disabled Projects</span>
          </NavLink>
        </li>

        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/leads`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/leads`}>
            <Icon icon="mdi:leads-outline" style={{ width: '30px', height: '30px' }} />
            <span className="menu-title mt-2">Leads</span>
          </NavLink>
        </li>

        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/shortupdates`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/shortupdates`}>
            <Icon icon="mdi:developer-board" style={{ width: '30px', height: '30px' }} />
            <span className="menu-title mt-2">Short Updates</span>
          </NavLink>
        </li>

        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/developers`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/developers`}>
            <span className="menu-title">Developers</span>
            <i className="mdi mdi-home menu-icon" />
          </NavLink>
        </li>

        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/offers`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/offers`}>
            <span className="menu-title">Offers</span>
            <i className="mdi mdi-home menu-icon" />
          </NavLink>
        </li>

        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/couponlist`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/couponlist`}>
            <span className="menu-title">All Coupons</span>
            <i className="mdi mdi-home menu-icon" />
          </NavLink>
        </li>

        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/locations`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/locations`}>
            <span className="menu-title">Location</span>
            <i className="mdi mdi-home menu-icon" />
          </NavLink>
        </li>

        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/seo-metadata`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/seo-metadata`}>
            <span className="menu-title">SEO Metadata</span>
            <i className="mdi mdi-home menu-icon" />
          </NavLink>
        </li>

        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/users`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/users`}>
            <span className="menu-title">Role Edit</span>
            <i className="mdi mdi-home menu-icon" />
          </NavLink>
        </li>

        {/* Developer Projects */}
        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/developer-projects`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/developer-projects`}>
            <Icon icon="mdi:folder-outline" style={{ width: '30px', height: '30px' }} />
            <span className="menu-title mt-2">Developer Projects</span>
          </NavLink>
        </li>

        {/* Locality Configurations */}
        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/locality-configurations`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/locality-configurations`}>
            <Icon icon="mdi:map-marker-multiple" style={{ width: '30px', height: '30px' }} />
            <span className="menu-title mt-2">Locality Configurations</span>
          </NavLink>
        </li>
        {/* uploade Excel File */}
         <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/upload-excel`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/upload-excel`}>
            <Icon icon="mdi:map-marker-multiple" style={{ width: '30px', height: '30px' }} />
            <span className="menu-title mt-2">Upload Excel</span>
          </NavLink>
        </li>

        

        
        {/* Project Media Report */}
        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/project-media-report`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/project-media-report`}>
            <Icon icon="mdi:file-document-multiple-outline" style={{ width: '30px', height: '30px' }} />
            <span className="menu-title mt-2">Project Media Report</span>
          </NavLink>
        </li>

        {/* Project Price Report */}
        <li className={`nav-item ${isPathActive(`${appAbsPath}/portaladmin/project-price-report`) ? 'active' : ''}`}>
          <NavLink className="nav-link" to={`${appAbsPath}/portaladmin/project-price-report`}>
            <Icon icon="mdi:currency-inr" style={{ width: '30px', height: '30px' }} />
            <span className="menu-title mt-2">Project Price Report</span>
          </NavLink>
        </li>

      </ul>
    </nav>
  );
};

export default Sidebar;
